import React from "react";
import { motion } from "framer-motion";

/**
 * A super simple chart visualization component that just shows one specific step of chart creation.
 * No internal state or navigation - completely controlled by parent.
 */
const ChartVisualization = ({ stepData }) => {
  // Sample data - hardcoded for simplicity
  const data = [
    { category: "Brown", value: 12, color: "#8B4513" },
    { category: "Blue", value: 8, color: "#6FA4FF" },
    { category: "Green", value: 5, color: "#4CAF50" },
    { category: "Hazel", value: 3, color: "#CD853F" },
  ];

  // Chart dimensions
  const width = Math.min(400, window.innerWidth * 0.9);
  const height = 250;

  // If no step data is provided, show a message
  if (!stepData) {
    return (
      <div className="w-full max-w-full mx-auto bg-white p-4 rounded-xl shadow-sm">
        <div className="text-center py-12">
          <p className="text-[#0D065B]">
            Chart visualization data not available.
          </p>
        </div>
      </div>
    );
  }

  // Helper to determine what to show at each step
  const getStepVisibility = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return {
          axes: true,
          scale: false,
          labels: false,
          title: false,
          bars: false,
          colors: false,
        };
      case 2:
        return {
          axes: true,
          scale: true,
          labels: false,
          title: false,
          bars: false,
          colors: false,
        };
      case 3:
        return {
          axes: true,
          scale: true,
          labels: true,
          title: false,
          bars: false,
          colors: false,
        };
      case 4:
        return {
          axes: true,
          scale: true,
          labels: true,
          title: true,
          bars: false,
          colors: false,
        };
      case 5:
        return {
          axes: true,
          scale: true,
          labels: true,
          title: true,
          bars: true,
          colors: false,
        };
      case 6:
        return {
          axes: true,
          scale: true,
          labels: true,
          title: true,
          bars: true,
          colors: true,
        };
      case 7:
        return {
          axes: true,
          scale: true,
          labels: true,
          title: true,
          bars: true,
          colors: true,
        }; // Complete
      default:
        return {
          axes: false,
          scale: false,
          labels: false,
          title: false,
          bars: false,
          colors: false,
        };
    }
  };

  // Get visibility config for current step
  const visibility = getStepVisibility(stepData.stepNumber);

  return (
    <div className="w-full max-w-full mx-auto bg-white p-4 rounded-xl shadow-sm">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-[#0D065B]">
            {stepData.title || "Chart Creation"}
          </h3>
          <div className="text-sm text-[#5F29FF]">
            Step {stepData.stepNumber || 1} of 7
          </div>
        </div>
        <p className="text-sm text-[#5F29FF]">
          {stepData.description || "Follow these steps to create a chart."}
        </p>
      </div>

      <div className="relative w-full mx-auto" style={{ height: height + 60 }}>
        {/* Chart background */}
        <div className="absolute inset-0 bg-[#F9F9FF] rounded-lg"></div>

        {/* Title */}
        {visibility.title && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-2 left-0 right-0 text-center text-[#0D065B] font-bold"
          >
            Eye Colors in Our Class
          </motion.div>
        )}

        {/* Axes */}
        {visibility.axes && (
          <>
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: height - 60 }}
              className="absolute bottom-8 left-10 w-1 bg-black"
              style={{ height: height - 60 }}
            />
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: width - 20 }}
              className="absolute bottom-8 left-10 h-1 bg-black"
              style={{ width: width - 20 }}
            />
          </>
        )}

        {/* Scale */}
        {visibility.scale && (
          <>
            {[0, 5, 10, 15].map((value, index) => {
              const yPos = height - 60 - (value / 15) * (height - 60);
              return (
                <motion.div
                  key={`scale-${index}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="absolute text-xs text-[#0D065B]"
                  style={{
                    bottom: yPos + 4,
                    left: 5,
                  }}
                >
                  {value}
                </motion.div>
              );
            })}
          </>
        )}

        {/* Category Labels */}
        {visibility.labels && (
          <>
            {data.map((item, index) => {
              const xPos = 10 + (index + 0.5) * ((width - 20) / data.length);
              return (
                <motion.div
                  key={`label-${index}`}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="absolute text-xs text-[#0D065B] text-center"
                  style={{
                    bottom: 0,
                    left: xPos,
                    width: (width - 20) / data.length,
                    transform: "translateX(-50%)",
                  }}
                >
                  {item.category}
                </motion.div>
              );
            })}
          </>
        )}

        {/* Bars */}
        {visibility.bars && (
          <>
            {data.map((item, index) => {
              const xPos = 10 + (index + 0.5) * ((width - 20) / data.length);
              const barWidth = (width - 40) / (data.length * 1.5);
              const barHeight = (item.value / 15) * (height - 60);
              const backgroundColor = visibility.colors
                ? item.color
                : "#AAAAAA";

              return (
                <motion.div
                  key={`bar-${index}`}
                  initial={{ height: 0 }}
                  animate={{ height: barHeight }}
                  transition={{ delay: index * 0.1 }}
                  className="absolute rounded-t-md"
                  style={{
                    bottom: 8,
                    left: xPos - barWidth / 2,
                    width: barWidth,
                    height: barHeight,
                    backgroundColor,
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="absolute -top-5 left-0 right-0 text-center text-xs text-[#0D065B]"
                  >
                    {item.value}
                  </motion.div>
                </motion.div>
              );
            })}
          </>
        )}
      </div>

      {/* Tip */}
      <div className="mt-6 bg-[#EFEAFC]/50 p-3 rounded-lg">
        <div className="flex items-start">
          <span className="text-xl mr-2">💡</span>
          <div>
            <strong className="text-[#0D065B] font-medium">Tip:</strong>
            <p className="text-sm">
              {stepData.tip ||
                "Follow each step carefully to create a great chart!"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartVisualization;
